import React, { useEffect, useState } from 'react';
import PageTitle from '../components/PageTitle';
import Api from '../api/api';
import BarChart from '../components/dashbaord/BarChart';
import LineChart from '../components/dashbaord/Linechart';
import { FaChartLine, FaTrophy, FaCalendarAlt, FaDollarSign, FaRocket, FaShieldAlt } from 'react-icons/fa';
import Spinner from '../components/common/Spinner';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

export default function Analytics() {
  const [metrics, setMetrics] = useState(null);
  const [pnlData, setPnlData] = useState([]);
  const [labels, setLabels] = useState([]);
  const [datas, setDatas] = useState([]);
  const [cumulativeDatas, setCumulativeDatas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30'); // 30, 60, 90 days

  useEffect(() => {
    loadAnalyticsData();
  }, [timeRange]);

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        getDashboardMetrics(),
        getPNLData()
      ]);
    } catch (error) {
      console.error('Error loading analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  async function getDashboardMetrics() {
    try {
      let res = await Api.dashboardMetrics();
      setMetrics(res?.data[0]);
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
    }
  }

  async function getPNLData() {
    try {
      let res = await Api.pnlChart(parseInt(timeRange));
      
      // Filter out future dates
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const filteredData = res?.data?.filter(item => {
        if (!item?.run_date) return false;
        const runDate = new Date(item.run_date);
        runDate.setHours(0, 0, 0, 0);
        return runDate <= today;
      }) || [];
      
      const labelsArray = filteredData.map(item => item?.run_date) || [];
      const datasArray = filteredData.map(item => parseFloat(item?.overall_pnl || 0)) || [];
      
      const cumulativeArray = filteredData.map(item => parseFloat(item?.cumulative_pnl || 0)).length > 0 && filteredData[0]?.cumulative_pnl !== undefined
        ? filteredData.map(item => parseFloat(item?.cumulative_pnl || 0))
        : datasArray.reduce((acc, val, idx) => {
            const prev = idx === 0 ? 0 : acc[idx - 1];
            return [...acc, prev + val];
          }, []);
      
      setLabels(labelsArray);
      setDatas(datasArray);
      setCumulativeDatas(cumulativeArray);
      setPnlData(filteredData);
    } catch (error) {
      console.error('Error fetching PNL data:', error);
    }
  }

  // Calculate win/loss distribution
  const winLossData = React.useMemo(() => {
    if (!datas || datas.length === 0) return null;
    
    const wins = datas.filter(val => val > 0).length;
    const losses = datas.filter(val => val < 0).length;
    const neutral = datas.filter(val => val === 0).length;
    
    return {
      labels: ['Winning Days', 'Losing Days', 'Neutral Days'],
      datasets: [{
        data: [wins, losses, neutral],
        backgroundColor: ['#28a745', '#dc3545', '#6c757d'],
        borderWidth: 0
      }]
    };
  }, [datas]);

  // Calculate profit distribution
  const profitDistribution = React.useMemo(() => {
    if (!datas || datas.length === 0) return null;
    
    const positive = datas.filter(val => val > 0);
    const negative = datas.filter(val => val < 0);
    
    const totalProfit = positive.reduce((sum, val) => sum + val, 0);
    const totalLoss = Math.abs(negative.reduce((sum, val) => sum + val, 0));
    
    return {
      labels: ['Total Profit', 'Total Loss'],
      datasets: [{
        data: [totalProfit, totalLoss],
        backgroundColor: ['#4DD5B8', '#FF823C'],
        borderWidth: 0
      }]
    };
  }, [datas]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spinner size="lg" text="Loading Analytics..." />
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <PageTitle title="Analytics" />
      
      {/* Header with Time Range Selector */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold mb-1" style={{ color: '#151B22' }}>Performance Analytics</h2>
          <p style={{ color: '#495057', fontSize: '15px', marginBottom: 0 }}>Detailed insights into your trading performance</p>
        </div>
        <select 
          className="form-select" 
          style={{ width: 'auto', borderRadius: '8px' }}
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
        >
          <option value="30">Last 30 days</option>
          <option value="60">Last 60 days</option>
          <option value="90">Last 90 days</option>
        </select>
      </div>

      {/* Key Metrics Cards */}
      <div className="row g-4 mb-4">
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '16px' }}>
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: '#4DD5B815',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#4DD5B8',
                  fontSize: '20px'
                }}>
                  <span style={{ fontSize: '20px', fontWeight: 'bold' }}>₹</span>
                </div>
              </div>
              <h6 className="mb-1" style={{ fontSize: '14px', textTransform: 'uppercase', color: '#6c757d', fontWeight: '600' }}>Total Profit</h6>
              <h4 className="fw-bold mb-0" style={{ color: '#151B22' }}>₹{metrics?.overall_pnl || 0}</h4>
              {metrics?.prev_overall_pnl && (
                <small style={{ fontSize: '14px', color: '#6c757d' }}>
                  {parseFloat(metrics.overall_pnl) > parseFloat(metrics.prev_overall_pnl) ? '↑' : '↓'} 
                  {Math.abs(((parseFloat(metrics.overall_pnl) - parseFloat(metrics.prev_overall_pnl)) / Math.abs(parseFloat(metrics.prev_overall_pnl) || 1)) * 100).toFixed(1)}% vs previous period
                </small>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '16px' }}>
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: '#FFC83315',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#FFC833',
                  fontSize: '20px'
                }}>
                  <FaTrophy />
                </div>
              </div>
              <h6 className="mb-1" style={{ fontSize: '14px', textTransform: 'uppercase', color: '#6c757d', fontWeight: '600' }}>Win Rate</h6>
              <h4 className="fw-bold mb-0" style={{ color: '#151B22' }}>{metrics?.win_pct || 0}%</h4>
              {metrics?.prev_win_pct && (
                <small style={{ fontSize: '14px', color: '#6c757d' }}>
                  {parseFloat(metrics.win_pct) > parseFloat(metrics.prev_win_pct) ? '↑' : '↓'} 
                  {Math.abs(parseFloat(metrics.win_pct) - parseFloat(metrics.prev_win_pct)).toFixed(1)}% vs previous period
                </small>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '16px' }}>
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: '#17a2b815',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#17a2b8',
                  fontSize: '20px'
                }}>
                  <FaCalendarAlt />
                </div>
              </div>
              <h6 className="mb-1" style={{ fontSize: '14px', textTransform: 'uppercase', color: '#6c757d', fontWeight: '600' }}>Trading Days</h6>
              <h4 className="fw-bold mb-0" style={{ color: '#151B22' }}>{metrics?.no_of_days_traded || 0}</h4>
              {metrics?.prev_no_of_days_traded && (
                <small style={{ fontSize: '14px', color: '#6c757d' }}>
                  {parseInt(metrics.no_of_days_traded) > parseInt(metrics.prev_no_of_days_traded) ? '↑' : '↓'} 
                  {Math.abs(parseInt(metrics.no_of_days_traded) - parseInt(metrics.prev_no_of_days_traded))} vs previous period
                </small>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '16px' }}>
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: '#28a74515',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#28a745',
                  fontSize: '20px'
                }}>
                  <FaChartLine />
                </div>
              </div>
              <h6 className="mb-1" style={{ fontSize: '14px', textTransform: 'uppercase', color: '#6c757d', fontWeight: '600' }}>Avg P&L/Day</h6>
              <h4 className="fw-bold mb-0" style={{ color: '#151B22' }}>₹{parseFloat(metrics?.avg_pnl || 0).toFixed(2)}</h4>
              {metrics?.prev_avg_pnl && (
                <small style={{ fontSize: '14px', color: '#6c757d' }}>
                  {parseFloat(metrics.avg_pnl) > parseFloat(metrics.prev_avg_pnl) ? '↑' : '↓'} 
                  ₹{Math.abs(parseFloat(metrics.avg_pnl) - parseFloat(metrics.prev_avg_pnl)).toFixed(2)} vs previous period
                </small>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="row g-4 mb-4">
        {/* Daily P&L Chart */}
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
            <div className="card-body">
              <h5 className="fw-bold mb-4" style={{ color: '#151B22' }}>Daily Profit & Loss</h5>
              {datas.length > 0 && labels.length > 0 ? (
                <div style={{ height: '300px' }}>
                  <BarChart datas={datas} labels={labels} />
                </div>
              ) : (
                <div className="text-center py-5">
                  <p style={{ color: '#495057', fontSize: '15px' }}>No data available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Cumulative P&L Chart */}
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
            <div className="card-body">
              <h5 className="fw-bold mb-4" style={{ color: '#151B22' }}>Cumulative Profit</h5>
              {cumulativeDatas.length > 0 && labels.length > 0 ? (
                <div style={{ height: '300px' }}>
                  <LineChart datas={cumulativeDatas} labels={labels} />
                </div>
              ) : (
                <div className="text-center py-5">
                  <p style={{ color: '#495057', fontSize: '15px' }}>No data available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Distribution Charts */}
      <div className="row g-4 mb-4">
        {/* Win/Loss Distribution */}
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
            <div className="card-body">
              <h5 className="fw-bold mb-4" style={{ color: '#151B22' }}>Win/Loss Distribution</h5>
              {winLossData ? (
                <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Doughnut data={winLossData} options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom'
                      }
                    }
                  }} />
                </div>
              ) : (
                <div className="text-center py-5">
                  <p style={{ color: '#495057', fontSize: '15px' }}>No data available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Profit/Loss Distribution */}
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
            <div className="card-body">
              <h5 className="fw-bold mb-4" style={{ color: '#151B22' }}>Profit vs Loss Amount</h5>
              {profitDistribution ? (
                <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Doughnut data={profitDistribution} options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom'
                      }
                    }
                  }} />
                </div>
              ) : (
                <div className="text-center py-5">
                  <p style={{ color: '#495057', fontSize: '15px' }}>No data available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Additional Stats */}
      <div className="row g-4">
        <div className="col-md-4">
          <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
            <div className="card-body">
              <h6 className="mb-3" style={{ fontSize: '14px', textTransform: 'uppercase', color: '#6c757d', fontWeight: '600' }}>Best Day</h6>
              {datas.length > 0 ? (
                <>
                  <h4 className="fw-bold mb-1" style={{ color: '#28a745' }}>
                    ₹{Math.max(...datas).toFixed(2)}
                  </h4>
                  <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: 0 }}>
                    {labels[datas.indexOf(Math.max(...datas))]}
                  </p>
                </>
              ) : (
                <p style={{ color: '#495057', fontSize: '15px' }}>No data available</p>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
            <div className="card-body">
              <h6 className="mb-3" style={{ fontSize: '14px', textTransform: 'uppercase', color: '#6c757d', fontWeight: '600' }}>Worst Day</h6>
              {datas.length > 0 ? (
                <>
                  <h4 className="fw-bold mb-1" style={{ color: '#dc3545' }}>
                    ₹{Math.min(...datas).toFixed(2)}
                  </h4>
                  <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: 0 }}>
                    {labels[datas.indexOf(Math.min(...datas))]}
                  </p>
                </>
              ) : (
                <p style={{ color: '#495057', fontSize: '15px' }}>No data available</p>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
            <div className="card-body">
              <h6 className="mb-3" style={{ fontSize: '14px', textTransform: 'uppercase', color: '#6c757d', fontWeight: '600' }}>Current Streak</h6>
              <h4 className="fw-bold mb-1" style={{ color: '#151B22' }}>
                {metrics?.streak || 0} days
              </h4>
              <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: 0 }}>Winning streak</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

